import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import React, { useEffect, useRef, useState } from "react";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { isPrivilegedUser } from "../../../Common/permissions";
import TaskDetails from "./TaskDetails";

const DeveloperTaskHistory = () => {
  // Reference
  const toast = useRef(null);

  // State for date selection and filters
  const [date, setDate] = useState(new Date());
  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [viewType, setViewType] = useState("timeline"); // 'timeline' or 'list'

  // Data states
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [developerOptions, setDeveloperOptions] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailsVisible, setTaskDetailsVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Admins and department heads can view any developer's history; a normal
  // member is locked to their own tasks only.
  const privileged = isPrivilegedUser(currentUser);

  // View types
  const viewTypeOptions = [
    { label: "Timeline View", value: "timeline" },
    { label: "List View", value: "list" },
  ];

  useEffect(() => {
    // Initial data loading
    fetchCurrentUser();
    fetchDevelopers();
  }, []);

  // Load the logged-in user; normal members are pinned to their own history.
  const fetchCurrentUser = async () => {
    try {
      const res = await Http.get("/me");
      const user = res.data?.data || res.data;
      setCurrentUser(user);
      if (!isPrivilegedUser(user) && user?.id) {
        setSelectedDeveloper(user.id);
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  // Fetch tasks when date or developer changes
  useEffect(() => {
    if (selectedDeveloper) {
      fetchTasksData();
    } else {
      setTasks([]);
      setLoading(false);
    }
  }, [date, selectedDeveloper]);

  const fetchDevelopers = async () => {
    try {
      // Fetch developers (staff) data - Assuming department ID 30 is for developers
      const staffResponse = await Http.get("/staff-department-wise/30");
      if (Array.isArray(staffResponse.data)) {
        const developersList = staffResponse.data.map((dev) => {
          const fullName = `${dev.first_name || ""} ${dev.last_name || ""}`.trim();
          return {
            id: dev.id,
            name: fullName || dev.name || `ID: ${dev.id}`,
          };
        });
        setDeveloperOptions(developersList.map((dev) => ({ label: dev.name, value: dev.id })));
      }
    } catch (error) {
      console.error("Error fetching developers:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load developers",
        life: 3000,
      });
    }
  };

  const normalizeTask = (task) => {
    // Create a shallow copy of the task
    const normalizedTask = { ...task };

    // Check if we need to standardize the statusHistory property
    if (!normalizedTask.statusHistory && normalizedTask.status_history) {
      normalizedTask.statusHistory = normalizedTask.status_history;
    }

    // Normalize any other properties if needed
    // For example, making sure developers property exists
    if (!normalizedTask.developers) {
      normalizedTask.developers = [];
    }

    return normalizedTask;
  };

  const fetchTasksData = async () => {
    setLoading(true);
    try {
      // Format date in YYYY-MM-DD format
      const d = new Date(date);
      const formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      console.log(`Fetching tasks for date: ${formattedDate} and developer: ${selectedDeveloper}`);

      // Fetch tasks for the selected date
      const response = await Http.get(`/tasks/calendar?date=${formattedDate}`);

      // Check if tasks are in response.data.data or directly in response.data
      const rawTasksData = response.data && response.data.data ? response.data.data : Array.isArray(response.data) ? response.data : [];

      // Normalize all tasks to have consistent property names
      const tasksData = rawTasksData.map(normalizeTask);

      console.log(`Received ${tasksData.length} tasks from API`);

      // Filter tasks for the selected developer
      let developerTasks = tasksData.filter((task) => {
        return task.developers && task.developers.some((dev) => dev.id === selectedDeveloper);
      });

      console.log(`Filtered to ${developerTasks.length} tasks for selected developer`);

      // Verify and log status history availability for each task
      developerTasks.forEach((task) => {
        const history = task.statusHistory || [];
        console.log(`Task ID: ${task.id}, Title: "${task.title}", Status: ${task.status}`);
        console.log(`Status history available: ${history.length} entries`);

        if (history.length > 0) {
          // Sort history by date for logging
          const sortedHistory = [...history].sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at));

          console.log("Status history timeline:");
          sortedHistory.forEach((entry, idx) => {
            console.log(`  ${idx + 1}. ${entry.old_status || "Initial"} → ${entry.new_status} (${new Date(entry.changed_at).toLocaleString()})`);
          });
        }
      });

      // Sort tasks by start time (using actual_start_date if available, otherwise start_date)
      developerTasks.sort((a, b) => {
        const startA = a.actual_start_date ? new Date(a.actual_start_date) : new Date(a.start_date);
        const startB = b.actual_start_date ? new Date(b.actual_start_date) : new Date(b.start_date);
        return startA - startB;
      });

      setTasks(developerTasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load tasks. " + (error.response?.data?.message || error.message),
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setTaskDetailsVisible(true);
  };

  // Function to get color for a status
  const getStatusColor = (status) => {
    switch (status) {
      case "To Do":
        return "#6c757d"; // secondary/gray
      case "In Progress":
        return "#007bff"; // primary/blue
      case "Review":
        return "#ffc107"; // warning/yellow
      case "Done":
        return "#28a745"; // success/green
      case "Pause":
        return "#dc3545"; // danger/red
      default:
        return "#6c757d"; // secondary/gray
    }
  };

  // Format date for display
  const formatDateTime = (dateObj) => {
    if (!dateObj) return "N/A";
    return new Date(dateObj).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // Format time only
  const formatTime = (dateObj) => {
    if (!dateObj) return "N/A";
    return new Date(dateObj).toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // Calculate task duration in hours
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "In progress";
    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationMs = end - start;
    const durationHours = durationMs / (1000 * 60 * 60);

    if (durationHours < 1) {
      const durationMinutes = Math.round(durationHours * 60);
      return `${durationMinutes} min${durationMinutes !== 1 ? "s" : ""}`;
    }

    return `${durationHours.toFixed(1)} hour${durationHours !== 1 ? "s" : ""}`;
  };

  // Get developer name from ID
  const getDeveloperName = (id) => {
    const developer = developerOptions.find((dev) => dev.value === id);
    return developer ? developer.label : `Developer ID: ${id}`;
  };

  // Calculate current time indicator position
  const getCurrentTimePosition = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTime = hours + minutes / 60;

    // Office hours: 9:30 AM - 7:30 PM
    const officeHoursStart = 9.5; // 9:30 AM
    const officeHoursEnd = 19.5; // 7:30 PM
    const officeHoursLength = officeHoursEnd - officeHoursStart;

    // If outside office hours, don't show
    if (currentTime < officeHoursStart || currentTime > officeHoursEnd) {
      return { left: "-100%" }; // Hide off-screen
    }

    // Calculate position within office hours
    const positionInOfficeHours = currentTime - officeHoursStart;
    const percentage = (positionInOfficeHours / officeHoursLength) * 100;

    return {
      left: `${percentage}%`,
      time: formatTime(now),
    };
  };

  // Format dates in a timezone-safe way
  const formatDateToYYYYMMDD = (dateObj) => {
    if (!dateObj) return null; // Handle cases where date might be null
    const d = new Date(dateObj);
    // Check for invalid date object
    if (isNaN(d.getTime())) {
      console.warn("Invalid date object passed to formatDateToYYYYMMDD:", dateObj);
      return null;
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Calculate days between two dates
  const daysBetween = (startDateStr, endDateStr) => {
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    // Reset time parts to ensure accurate day calculation
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    // Calculate difference in days
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get all status segments for a task on a specific date
  const getDailyStatusSegments = (task, dateStr) => {
    // Get the status history and sort it
    const statusHistory = task.statusHistory || [];
    if (!Array.isArray(statusHistory) || statusHistory.length === 0) {
      // If no status history, return single segment with current status
      return [
        {
          status: task.status,
          color: getStatusColor(task.status),
          startTime: new Date(`${dateStr}T09:30:00`), // Office start
          endTime: new Date(`${dateStr}T19:30:00`), // Office end
          width: "100%",
        },
      ];
    }

    // Create date objects for the start and end of the selected day
    const dayStart = new Date(`${dateStr}T00:00:00`);
    const dayEnd = new Date(`${dateStr}T23:59:59`);
    const nextDay = new Date(dayStart);
    nextDay.setDate(nextDay.getDate() + 1);

    // Office hours for display purposes
    const officeStart = new Date(`${dateStr}T09:30:00`);
    const officeEnd = new Date(`${dateStr}T19:30:00`);

    // Sort all history
    const sortedHistory = [...statusHistory].sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at));

    // Find the status at the beginning of the day (the last status before the selected day)
    let initialStatus = null;

    // Look for the last status change before the current day
    for (let i = sortedHistory.length - 1; i >= 0; i--) {
      const statusDate = new Date(sortedHistory[i].changed_at);
      if (statusDate < dayStart) {
        initialStatus = sortedHistory[i].new_status;
        break;
      }
    }

    // If no previous status was found, use the first status in history or current status
    if (!initialStatus) {
      if (sortedHistory.length > 0) {
        initialStatus = sortedHistory[0].new_status;
      } else {
        initialStatus = task.status;
      }
    }

    // Filter status changes that occurred on this day
    const dayStatusChanges = sortedHistory.filter((entry) => {
      const changeTime = new Date(entry.changed_at);
      return changeTime >= dayStart && changeTime < nextDay;
    });

    // Create segments for the day
    const segments = [];

    // Always start with the status from the end of the previous day
    if (initialStatus && initialStatus !== "To Do") {
      // Find the first status change of the day, if any
      const firstChangeOfDay = dayStatusChanges.length > 0 ? new Date(dayStatusChanges[0].changed_at) : officeEnd;

      // Only add if the segment would actually display during office hours
      if (officeStart < firstChangeOfDay) {
        // Clip end time to office hours if needed
        const segmentEndTime = firstChangeOfDay > officeEnd ? officeEnd : firstChangeOfDay;

        const duration = segmentEndTime.getTime() - officeStart.getTime();

        if (duration > 0) {
          segments.push({
            status: initialStatus,
            color: getStatusColor(initialStatus),
            startTime: officeStart,
            endTime: segmentEndTime,
            duration: duration,
          });
        }
      }
    }

    // Add segments for each status change during the day
    dayStatusChanges.forEach((change, idx) => {
      const segmentStartTime = new Date(change.changed_at);

      // End time is next status change or end of office hours
      let segmentEndTime;
      if (idx < dayStatusChanges.length - 1) {
        segmentEndTime = new Date(dayStatusChanges[idx + 1].changed_at);
      } else {
        segmentEndTime = officeEnd;
      }

      // Skip "To Do" status entirely - we only want to show active statuses
      if (change.new_status === "To Do") {
        return;
      }

      // Handle segments outside office hours
      if (segmentStartTime >= officeEnd || segmentEndTime <= officeStart) {
        // Skip segments entirely outside office hours
        return;
      }

      // Clip segment times to office hours
      const actualStartTime = segmentStartTime < officeStart ? officeStart : segmentStartTime;
      const actualEndTime = segmentEndTime > officeEnd ? officeEnd : segmentEndTime;

      const duration = actualEndTime.getTime() - actualStartTime.getTime();

      if (duration > 0) {
        segments.push({
          status: change.new_status,
          color: getStatusColor(change.new_status),
          startTime: actualStartTime,
          endTime: actualEndTime,
          duration: duration,
        });
      }
    });

    // If we have no segments (e.g., only "To Do" status), return an empty array
    if (segments.length === 0) {
      return [];
    }

    // Calculate total duration of all segments
    const totalSegmentsDuration = segments.reduce((total, segment) => total + segment.duration, 0);

    // Ensure very small segments are still visible (minimum width)
    return segments.map((segment) => {
      // If segment is less than 1.5% of the total segments time, give it a minimum width for visibility
      const minWidth = 1.5;
      const calculatedWidth = (segment.duration / totalSegmentsDuration) * 100;
      const width = calculatedWidth < minWidth ? `${minWidth}%` : `${calculatedWidth}%`;

      return {
        ...segment,
        width,
        // Add a flag for very short durations to show in tooltip
        isVeryShort: calculatedWidth < minWidth,
        // Keep the original calculated width for accurate distribution
        calculatedWidth,
      };
    });
  };

  const renderTimelineView = () => {
    if (tasks.length === 0) {
      return (
        <div className="empty-state p-4 text-center">
          <i className="pi pi-calendar-times" style={{ fontSize: "3rem", color: "#ddd" }}></i>
          <h3>No tasks found</h3>
          <p>There are no tasks for this developer on the selected date.</p>
        </div>
      );
    }

    // Office hours settings
    const officeHoursStart = 9.5; // 9:30 AM
    const officeHoursEnd = 19.5; // 7:30 PM
    const officeHoursLength = officeHoursEnd - officeHoursStart;

    // Generate hour markers for office hours
    const hourMarkers = [];
    for (let hour = 9; hour <= 19; hour++) {
      if (hour === 9) {
        hourMarkers.push("9:30 AM");
      } else if (hour === 19) {
        hourMarkers.push("7:30 PM");
      } else {
        hourMarkers.push(`${hour > 12 ? hour - 12 : hour}${hour >= 12 ? " PM" : " AM"}`);
      }
    }

    // Ensure tasks are sorted by start time
    const sortedTasks = [...tasks].sort((a, b) => {
      const startA = a.actual_start_date ? new Date(a.actual_start_date) : new Date(a.start_date);
      const startB = b.actual_start_date ? new Date(b.actual_start_date) : new Date(b.start_date);
      return startA - startB;
    });

    // Calculate current time indicator position
    const currentTimePosition = getCurrentTimePosition();
    const isToday = new Date(date).toDateString() === new Date().toDateString();

    // Selected date string for comparison
    const selectedDateStr = formatDateToYYYYMMDD(date);

    return (
      <div className="timeline-container">
        <div className="time-scale">
          {hourMarkers.map((marker, i) => (
            <div key={i} className="hour-marker">
              <span>{marker}</span>
            </div>
          ))}
        </div>

        <div className="tasks-timeline">
          {/* Current time indicator - only show if the selected date is today */}
          {isToday && (
            <div className="current-time-indicator" style={{ left: currentTimePosition.left }}>
              <div className="current-time-label">{currentTimePosition.time}</div>
            </div>
          )}

          {sortedTasks.map((task, taskIndex) => {
            // Determine task dates for proper positioning
            const taskStartDateStr = task.actual_start_date
              ? formatDateToYYYYMMDD(new Date(task.actual_start_date))
              : formatDateToYYYYMMDD(new Date(task.start_date));
            const taskEndDateStr = task.actual_end_date
              ? formatDateToYYYYMMDD(new Date(task.actual_end_date))
              : formatDateToYYYYMMDD(new Date(task.end_date));

            // Calculate days running for continuing tasks
            const daysRunning = taskStartDateStr && taskStartDateStr < selectedDateStr ? daysBetween(taskStartDateStr, selectedDateStr) : 0;

            let startDate, endDate;

            // Handle task display for the selected day
            if (taskStartDateStr === selectedDateStr) {
              // Task starts on the selected day - use actual time if available
              startDate = task.actual_start_date ? new Date(task.actual_start_date) : new Date(`${selectedDateStr}T09:30:00`);
            } else if (taskStartDateStr < selectedDateStr) {
              // Task started before the selected day - show from beginning of office hours
              startDate = new Date(`${selectedDateStr}T09:30:00`);
            } else {
              // Fallback (should not happen with our query, but just in case)
              startDate = new Date(`${selectedDateStr}T09:30:00`);
            }

            if (taskEndDateStr === selectedDateStr && task.actual_end_date) {
              // Task ends on the selected day - use actual time if available
              endDate = new Date(task.actual_end_date);
            } else if (taskEndDateStr > selectedDateStr || !task.actual_end_date) {
              // Task continues beyond selected day or is not completed - show until end of office hours
              endDate = new Date(`${selectedDateStr}T19:30:00`);
            } else {
              // Fallback
              endDate = new Date(`${selectedDateStr}T19:30:00`);
            }

            // Calculate position within office hours
            const dayStart = new Date(startDate);
            dayStart.setHours(officeHoursStart, 30, 0, 0);

            const dayEnd = new Date(dayStart);
            dayEnd.setHours(officeHoursEnd, 30, 0, 0);

            const startTime = startDate.getHours() + startDate.getMinutes() / 60;
            const endTime = endDate.getHours() + endDate.getMinutes() / 60;

            // Adjust hours to fit in our display window (9:30am-7:30pm)
            const adjustedStartTime = Math.max(officeHoursStart, Math.min(officeHoursEnd, startTime));
            const adjustedEndTime = Math.min(officeHoursEnd, Math.max(adjustedStartTime + 0.5, endTime));

            const startPositionInDay = adjustedStartTime - officeHoursStart;
            const durationInDay = adjustedEndTime - adjustedStartTime;

            const leftPosition = (startPositionInDay / officeHoursLength) * 100;
            const widthPercentage = (durationInDay / officeHoursLength) * 100;

            // Flag for continuing task
            const isContinuingTask = taskStartDateStr < selectedDateStr;

            // Get all status segments for this day
            const segments = getDailyStatusSegments(task, selectedDateStr);

            return (
              <div key={task.id} className="task-row">
                <div className="task-label">
                  <div className="task-row-time">
                    {formatTime(startDate)}
                    {isContinuingTask && (
                      <span className="days-running-badge" title={`This task has been running for ${daysRunning} day${daysRunning !== 1 ? "s" : ""}`}>
                        {daysRunning}d
                      </span>
                    )}
                  </div>
                  <div className="task-row-title">{task.title}</div>
                  <div className="task-row-project">{task.project?.title || "Unassigned"}</div>
                </div>
                <div className="task-timeline-wrapper">
                  <div
                    className={`task-item ${isContinuingTask ? "continuing-task" : ""}`}
                    onClick={() => handleTaskClick(task)}
                    style={{ left: `${leftPosition}%`, width: `${widthPercentage}%` }}
                  >
                    <div id={`task-${task.id}`} className="task-bar-container">
                      <div className="task-segments">
                        {segments.map((segment, idx) => (
                          <div
                            key={idx}
                            className="task-segment"
                            style={{
                              width: segment.width,
                              backgroundColor: segment.color,
                              borderTopRightRadius: idx === segments.length - 1 ? "4px" : "0px",
                              borderBottomRightRadius: idx === segments.length - 1 ? "4px" : "0px",
                              borderTopLeftRadius: idx === 0 ? "4px" : "0px",
                              borderBottomLeftRadius: idx === 0 ? "4px" : "0px",
                            }}
                            title={`${segment.status}${segment.isVeryShort ? " (brief)" : ""}: ${formatTime(segment.startTime)} - ${formatTime(
                              segment.endTime
                            )}
Duration: ${calculateDuration(segment.startTime, segment.endTime)}`}
                          ></div>
                        ))}
                      </div>
                      <div className="task-content-history-panel">
                        <div className="task-title">
                          {isContinuingTask && (
                            <span className="continuing-indicator" title={`Started ${daysRunning} day${daysRunning !== 1 ? "s" : ""} ago`}>
                              ⟳
                            </span>
                          )}
                          {task.title}
                        </div>
                        <div className="task-time">
                          {formatTime(startDate)} - {task.actual_end_date ? formatTime(endDate) : "now"}
                        </div>
                      </div>
                    </div>
                    <Tooltip target={`#task-${task.id}`} position="top">
                      <div>
                        <strong>{task.title}</strong>
                        <div>{task.project?.title}</div>
                        <div>Start: {formatTime(startDate)}</div>
                        <div>End: {task.actual_end_date ? formatTime(endDate) : "In progress"}</div>
                        <div>Status: {task.status}</div>
                        {isContinuingTask && (
                          <div className="continuing-info">
                            Started {daysRunning} day{daysRunning !== 1 ? "s" : ""} ago on{" "}
                            {new Date(task.actual_start_date || task.start_date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                  {/* Add grid lines to the timeline */}
                  <div className="timeline-grid">
                    {hourMarkers.map((_, i) => (
                      <div key={i} className="grid-line"></div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderListView = () => {
    if (tasks.length === 0) {
      return (
        <div className="empty-state p-4 text-center">
          <i className="pi pi-calendar-times" style={{ fontSize: "3rem", color: "#ddd" }}></i>
          <h3>No tasks found</h3>
          <p>There are no tasks for this developer on the selected date.</p>
        </div>
      );
    }

    // Ensure tasks are sorted by start time
    const sortedTasks = [...tasks].sort((a, b) => {
      const startA = a.actual_start_date ? new Date(a.actual_start_date) : new Date(a.start_date);
      const startB = b.actual_start_date ? new Date(b.actual_start_date) : new Date(b.start_date);
      return startA - startB;
    });

    // Selected date string for comparison
    const selectedDateStr = formatDateToYYYYMMDD(date);

    return (
      <div className="tasks-list">
        <div className="task-list-header">
          <div className="col-time">Start Time</div>
          <div className="col-time">End Time</div>
          <div className="col-title">Task</div>
          <div className="col-project">Project</div>
          <div className="col-duration">Duration</div>
          <div className="col-status">Status History</div>
        </div>

        {sortedTasks.map((task) => {
          // Determine task dates for proper positioning
          const taskStartDateStr = task.actual_start_date
            ? formatDateToYYYYMMDD(new Date(task.actual_start_date))
            : formatDateToYYYYMMDD(new Date(task.start_date));
          const taskEndDateStr = task.actual_end_date
            ? formatDateToYYYYMMDD(new Date(task.actual_end_date))
            : formatDateToYYYYMMDD(new Date(task.end_date));

          // Calculate days running for continuing tasks
          const daysRunning = taskStartDateStr && taskStartDateStr < selectedDateStr ? daysBetween(taskStartDateStr, selectedDateStr) : 0;

          // Flag for continuing task
          const isContinuingTask = taskStartDateStr < selectedDateStr;

          let startDate, endDate;

          // Handle task display for the selected day
          if (taskStartDateStr === selectedDateStr) {
            // Task starts on the selected day - use actual time if available
            startDate = task.actual_start_date ? new Date(task.actual_start_date) : new Date(`${selectedDateStr}T09:30:00`);
          } else if (taskStartDateStr < selectedDateStr) {
            // Task started before the selected day - show from beginning of office hours
            startDate = new Date(`${selectedDateStr}T09:30:00`);
          } else {
            // Fallback (should not happen with our query, but just in case)
            startDate = new Date(`${selectedDateStr}T09:30:00`);
          }

          if (taskEndDateStr === selectedDateStr && task.actual_end_date) {
            // Task ends on the selected day - use actual time if available
            endDate = new Date(task.actual_end_date);
          } else if (taskEndDateStr > selectedDateStr || !task.actual_end_date) {
            // Task continues beyond selected day or is not completed - show until end of office hours
            endDate = new Date(`${selectedDateStr}T19:30:00`);
          } else {
            // Fallback
            endDate = new Date(`${selectedDateStr}T19:30:00`);
          }

          // Get all status segments for this day
          const segments = getDailyStatusSegments(task, selectedDateStr);

          return (
            <div key={task.id} className={`task-list-item ${isContinuingTask ? "continuing-list-item" : ""}`} onClick={() => handleTaskClick(task)}>
              <div className="col-time">
                {formatTime(startDate)}
                {isContinuingTask && (
                  <span className="days-running-badge" title={`This task has been running for ${daysRunning} day${daysRunning !== 1 ? "s" : ""}`}>
                    {daysRunning}d
                  </span>
                )}
              </div>
              <div className="col-time">{task.actual_end_date ? formatTime(endDate) : "In progress"}</div>
              <div className="col-title">
                {isContinuingTask && <span className="list-continuing-indicator">⟳</span>}
                {task.title}
              </div>
              <div className="col-project">{task.project?.title || "Unassigned"}</div>
              <div className="col-duration">{calculateDuration(startDate, task.actual_end_date ? endDate : null)}</div>
              <div className="col-status">
                <div className="status-timeline">
                  {segments.map((segment, idx) => (
                    <div
                      key={idx}
                      className="status-segment"
                      style={{
                        width: segment.width,
                        backgroundColor: segment.color,
                      }}
                      title={`${segment.status}${segment.isVeryShort ? " (brief)" : ""}: ${formatTime(segment.startTime)} - ${formatTime(
                        segment.endTime
                      )}
Duration: ${calculateDuration(segment.startTime, segment.endTime)}`}
                    ></div>
                  ))}
                </div>
                <div className="current-status" style={{ color: getStatusColor(segments[segments.length - 1].status) }}>
                  {segments[segments.length - 1].status}
                  {segments.length > 1 && <span className="status-changes-info"> ({segments.length} status changes today)</span>}
                  {isContinuingTask && (
                    <span className="list-start-date"> (started {new Date(task.actual_start_date || task.start_date).toLocaleDateString()})</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />

      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Developer Task History</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              {privileged ? (
                <Dropdown
                  value={selectedDeveloper}
                  options={developerOptions}
                  onChange={(e) => setSelectedDeveloper(e.value)}
                  placeholder="Select Developer"
                  className="mx-2"
                  style={{ minWidth: "250px" }}
                />
              ) : (
                <span className="mx-2 fw-bold" style={{ minWidth: "250px" }}>
                  {currentUser?.name || "My Task History"}
                </span>
              )}
              <Calendar value={date} onChange={(e) => setDate(e.value)} showIcon className="mx-2" />
              <Dropdown value={viewType} options={viewTypeOptions} onChange={(e) => setViewType(e.value)} className="mx-2" />
              <Button
                icon="pi pi-refresh"
                className="p-button-outlined mx-2"
                onClick={() => {
                  setDate(new Date());
                  fetchTasksData();
                }}
                tooltip="Refresh"
                tooltipOptions={{ position: "bottom" }}
              />
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card p-3">
            <div className="developer-info mb-3">
              {selectedDeveloper && <h2 className="developer-name">{getDeveloperName(selectedDeveloper)}</h2>}
              <h3 className="date-display">
                {date.toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </h3>
            </div>

            {loading ? (
              <div className="text-center p-5">
                <ProgressSpinner />
                <p className="mt-3">Loading task data...</p>
              </div>
            ) : (
              <div className="task-view-container">
                {!selectedDeveloper ? (
                  <div className="select-developer-prompt text-center p-5">
                    <i className="pi pi-user" style={{ fontSize: "3rem", color: "#ddd" }}></i>
                    <h3>Select a Developer</h3>
                    <p>Please select a developer to view their task history.</p>
                  </div>
                ) : viewType === "timeline" ? (
                  renderTimelineView()
                ) : (
                  renderListView()
                )}
              </div>
            )}

            {/* Legend */}
            <div className="status-legend mt-4">
              <div className="legend-title">Status Legend:</div>
              <div className="legend-items">
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: getStatusColor("To Do") }}></div>
                  <div className="legend-label">To Do</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: getStatusColor("In Progress") }}></div>
                  <div className="legend-label">In Progress</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: getStatusColor("Review") }}></div>
                  <div className="legend-label">Review</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: getStatusColor("Done") }}></div>
                  <div className="legend-label">Done</div>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: getStatusColor("Pause") }}></div>
                  <div className="legend-label">Pause</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header="Task Details"
        visible={taskDetailsVisible}
        style={{ width: "80%", maxWidth: "800px" }}
        onHide={() => setTaskDetailsVisible(false)}
        dismissableMask
      >
        {selectedTask && <TaskDetails task={selectedTask} />}
      </Dialog>

      <style>{`
        .timeline-container {
          position: relative;
          margin-top: 20px;
          overflow-x: auto;
          padding-top: 30px;
        }

        .time-scale {
          display: flex;
          position: sticky;
          top: 0;
          left: 180px;
          right: 0;
          height: 30px;
          border-bottom: 1px solid #ddd;
          background-color: #f8f9fa;
          z-index: 5;
        }

        .hour-marker {
          flex: 1;
          position: relative;
          height: 100%;
          text-align: center;
          border-right: 1px solid #eee;
        }

        .hour-marker span {
          font-size: 0.75rem;
          color: #777;
        }

        .tasks-timeline {
          position: relative;
          padding-top: 10px;
        }

        .task-row {
          display: flex;
          height: 80px;
          margin-bottom: 15px;
          position: relative;
          border-bottom: 1px solid #f0f0f0;
          padding-bottom: 15px;
        }

        .task-label {
          width: 180px;
          min-width: 180px;
          padding-right: 15px;
          overflow: hidden;
          position: sticky;
          left: 0;
          background-color: white;
          z-index: 2;
          border-right: 1px solid #ddd;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .task-row-time {
          font-size: 0.8rem;
          font-weight: 600;
          color: #007bff;
          margin-bottom: 4px;
          display: flex;
          align-items: center;
        }

        .days-running-badge {
          background-color: #007bff;
          color: white;
          padding: 1px 4px;
          border-radius: 4px;
          font-size: 0.7rem;
          margin-left: 5px;
          display: inline-block;
        }

        .task-row-title {
          font-weight: 500;
          font-size: 1rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-bottom: 4px;
        }

        .task-row-project {
          font-size: 0.8rem;
          color: #666;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .task-timeline-wrapper {
          flex-grow: 1;
          position: relative;
          height: 80px;
        }

        .task-item {
          position: absolute;
          height: 65px;
          cursor: pointer;
          transition: transform 0.2s ease;
          z-index: 2;
          top: 7px;
        }

        .task-item.continuing-task {
          border-left: 3px dashed rgba(255, 255, 255, 0.7);
        }

        .continuing-indicator {
          display: inline-block;
          margin-right: 4px;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .task-item:hover {
          transform: translateY(-2px);
          z-index: 3;
        }

        .task-bar-container {
          border-radius: 4px;
          height: 100%;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          position: relative;
        }

        .task-segments {
          display: flex;
          height: 100%;
          width: 100%;
          position: absolute;
          top: 0;
          left: 0;
          border-radius: 4px;
          overflow: hidden;
        }

        .task-segment {
          height: 100%;
          min-width: 3px; /* Ensure very small segments are visible */
        }

        .continuing-task {
          border-left: 4px solid #2196f3;
        }

        .task-content-history-panel {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 10px;
          color: white;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .task-title {
          font-weight: 500;
          font-size: 0.9rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .task-time {
          font-size: 0.8rem;
          opacity: 0.9;
        }

        .timeline-grid {
          display: flex;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
        }

        .grid-line {
          flex: 1;
          height: 100%;
          border-left: 1px dashed #eee;
        }

        /* List view styles */
        .tasks-list {
          margin-top: 20px;
          border: 1px solid #ddd;
          border-radius: 4px;
          overflow: hidden;
        }

        .task-list-header {
          display: flex;
          background-color: #f5f5f5;
          font-weight: 600;
          padding: 12px 15px;
          border-bottom: 1px solid #ddd;
        }

        .task-list-item {
          display: flex;
          border-bottom: 1px solid #eaeaea;
          padding: 10px 5px;
          transition: background-color 0.2s;
          cursor: pointer;
        }

        .task-list-item:hover {
          background-color: rgba(33, 150, 243, 0.05);
        }
        
        .continuing-list-item {
          border-left: 4px solid #2196f3;
          background-color: rgba(33, 150, 243, 0.05);
        }
        
        .continuing-list-item:hover {
          background-color: rgba(33, 150, 243, 0.12);
        }

        .task-list-item .col-time {
          width: 80px;
          font-size: 0.85rem;
          position: relative;
        }
        
        .task-list-item .days-running-badge {
          position: absolute;
          top: -10px;
          right: 10px;
          font-size: 0.7rem;
          background-color: #ff9800;
          color: white;
          border-radius: 10px;
          padding: 1px 5px;
          font-weight: bold;
        }

        .col-title {
          flex: 3;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .col-project {
          flex: 2;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .col-time {
          flex: 1;
          white-space: nowrap;
        }

        .col-duration {
          flex: 1;
          white-space: nowrap;
        }

        .col-status {
          flex: 2;
          display: flex;
          flex-direction: column;
        }

        .status-timeline {
          display: flex;
          height: 8px;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 5px;
          min-width: 100px; /* Ensure minimum width */
        }

        .status-segment {
          height: 100%;
          min-width: 3px; /* Ensure very small segments are visible */
        }

        .current-status {
          font-weight: 500;
        }

        /* Legend styles */
        .status-legend {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          border-top: 1px solid #eee;
          padding-top: 15px;
        }

        .legend-title {
          font-weight: 500;
          margin-right: 15px;
        }

        .legend-items {
          display: flex;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          margin-right: 15px;
        }

        .legend-color {
          width: 18px;
          height: 18px;
          border-radius: 4px;
          margin-right: 6px;
        }

        .legend-label {
          font-size: 0.9rem;
        }

        .developer-info {
          text-align: center;
        }

        .developer-name {
          font-size: 1.8rem;
          font-weight: 500;
          margin-bottom: 5px;
        }

        .date-display {
          font-size: 1.2rem;
          font-weight: 400;
          color: #666;
        }

        /* Add current time indicator */
        .current-time-indicator {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 2px;
          background-color: #f44336;
          z-index: 4;
        }

        .current-time-label {
          position: absolute;
          top: -20px;
          transform: translateX(-50%);
          background-color: #f44336;
          color: white;
          padding: 1px 4px;
          border-radius: 3px;
          font-size: 0.7rem;
        }

        .continuing-info {
          font-size: 0.8rem;
          font-style: italic;
          color: #555;
          margin-top: 3px;
        }

        .list-continuing-indicator {
          display: inline-block;
          margin-right: 4px;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .list-start-date {
          font-size: 0.8rem;
          font-style: italic;
          color: #555;
          margin-left: 5px;
        }

        .status-changes-info {
          font-size: 0.8rem;
          font-style: italic;
          color: #555;
          margin-left: 5px;
        }
      `}</style>
    </div>
  );
};

export default DeveloperTaskHistory;
